import React, { useState } from "react";
import {
  FormProvider,
  useForm,
  type FieldErrors,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";

import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { PageLayout } from "components/layout";

import {
  useCreateGamificationMutation,
  useLazyValidateGamificationPeriodQuery,
  useUploadSegmentationMutation,
} from "../../../../services/gamification";
import {
  createGamificationSchema,
  draftGamificationSchema,
  type CreateGamificationFormData,
} from "../validations/gamification.validation";
import { CampaignType, VoucherValidityMode } from "../../../../@types/gamification";
import { DEFAULT_CAMPAIGN_PRIORITY } from "../constants/priority-levels";
import { getDefaultFaqItems, getDefaultPrizes } from "../constants/mechanic-defaults";

import {
  getPendingIssues,
  REVIEW_STEP,
  type StepGuardOptions,
  WIZARD_STEPS,
  WizardStep,
} from "../wizard/steps";
import { GamificationWizard } from "../wizard/GamificationWizard";
import { UnsavedChangesDialog } from "../wizard/UnsavedChangesDialog";
import {
  LEAVE_WITHOUT_GUARD,
  useUnsavedChangesGuard,
} from "../wizard/useUnsavedChangesGuard";
import { firstErrorMessage, SUBMIT_BLOCKED_FALLBACK } from "../wizard/form-errors";
import { ReviewStep } from "./components/ReviewStep";
import { StepBasics } from "./components/steps/StepBasics";
import { StepConfig } from "./components/steps/StepConfig";
import { StepFiles } from "./components/steps/StepFiles";
import { StepPrizes } from "./components/steps/StepPrizes";
import { StepFaq } from "./components/steps/StepFaq";
import { StepSegmentation } from "./components/steps/StepSegmentation";
import { StepStores } from "./components/steps/StepStores";
import { useBannerState } from "../hooks/use-banner-state";
import { useCampaignSlugSuggestion } from "../hooks/use-campaign-slug-suggestion";
import {
  type SlugAvailability,
  useSlugAvailability,
} from "../hooks/use-slug-availability";
import { useLocalDraft } from "./hooks/useLocalDraft";
import { LocalDraftBanner } from "./components/LocalDraftBanner";
import { useFormSubmit } from "./hooks/useFormSubmit";
import { useSubmitAndClearDraft } from "./hooks/useSubmitAndClearDraft";
import { LOCKED_MIN_PURCHASE } from "../constants/mechanic-defaults";

const CREATE_GUARD_OPTIONS = {
  requireCoverImage: true,
  requireWideBanner: true,
  blockPastStart: true,
} as const;

export const CreateGamification: React.FC = () => {
  const navigate = useNavigate();
  const [createCampaign, { isLoading: isCreating }] =
    useCreateGamificationMutation();
  const [validatePeriod] = useLazyValidateGamificationPeriodQuery();
  const [uploadSegmentation] = useUploadSegmentationMutation();
  const [currentStep, setCurrentStep] = useState<number>(WizardStep.BASICS);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const form = useForm<CreateGamificationFormData>({
    resolver: zodResolver(createGamificationSchema),
    defaultValues: {
      campaignName: "",
      slug: "",
      campaignType: CampaignType.ROULETTE,
      startDate: "",
      endDate: "",
      stores: [],
      mechanicConfig: {
        prizes: getDefaultPrizes(CampaignType.ROULETTE),
        maxPlays: undefined,
        minPurchase: LOCKED_MIN_PURCHASE,
        priority: DEFAULT_CAMPAIGN_PRIORITY,
        concurrency: { competes: false, group: "" },
        voucherValidity: {
          mode: VoucherValidityMode.DAYS,
          validityDays: 30,
          startOffsetDays: 0,
        },
        cpfSegmentation: { enabled: false },
        mechanicParams: { goalProbability: undefined, symbols: [] },
        presentation: { customerFacingName: "", customerFacingDescription: "" },
      },
      segmentationFile: undefined,
      faqItems: getDefaultFaqItems(CampaignType.ROULETTE),
      regulations: undefined,
      banner3x4: undefined,
      banner16x9: undefined,
      banner3x4SourceKey: undefined,
      banner16x9SourceKey: undefined,
      allStoresSelected: false,
    },
  });

  const banners = useBannerState(form);

  const slugAvailability: SlugAvailability = useSlugAvailability(form.control);
  useCampaignSlugSuggestion(form, slugAvailability.status);
  const guardOptions: StepGuardOptions = {
    ...CREATE_GUARD_OPTIONS,
    slugStatus: slugAvailability.status,
  };

  const { onSubmit } = useFormSubmit({
    createCampaign,
    validatePeriod,
    uploadSegmentation,
    onSlugConflict: (message: string): void => {
      form.setError("slug", { type: "manual", message });
      setCurrentStep(WizardStep.CONFIG);
    },
  });

  const localDraft = useLocalDraft(form);

  const submitAndClearDraft = useSubmitAndClearDraft({
    onSubmit,
    clearDraft: localDraft.clear,
  });

  const handleInvalidSubmit = (errors: FieldErrors<CreateGamificationFormData>) => {
    const reason = firstErrorMessage(errors) ?? SUBMIT_BLOCKED_FALLBACK;
    toast({
      description: `Não foi possível criar a campanha: ${reason}`,
      variant: "destructive",
    });
  };

  const handleSaveDraft = async () => {
    if (isSavingDraft || isCreating) return;
    const values = form.getValues();
    const parsed = draftGamificationSchema.safeParse(values);
    if (!parsed.success) {
      toast({
        title: "Rascunho incompleto",
        description: parsed.error.issues[0]?.message,
        variant: "destructive",
      });
      return;
    }
    setIsSavingDraft(true);
    try {
      await submitAndClearDraft(values, { draft: true });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const pendingIssues = getPendingIssues(form.watch(), guardOptions);

  const leaveGuard = useUnsavedChangesGuard(
    form.formState.isDirty,
    () => navigate("/crm/gestao-gameficacao", { state: LEAVE_WITHOUT_GUARD }),
  );

  const renderStep = (step: number): React.ReactNode => {
    const byStep: Record<number, React.ReactNode> = {
      [WizardStep.BASICS]: <StepBasics />,
      [WizardStep.STORES]: <StepStores />,
      [WizardStep.PRIZES]: <StepPrizes />,
      [WizardStep.CONFIG]: <StepConfig slugAvailability={slugAvailability} />,
      [WizardStep.SEGMENTATION]: <StepSegmentation />,
      [WizardStep.FILES]: (
        <StepFiles cover={banners.cover} alternative={banners.alternative} />
      ),
      [WizardStep.FAQ]: <StepFaq />,
      [WizardStep.REVIEW]: (
        <ReviewStep
          values={form.getValues()}
          pendingIssues={pendingIssues}
          banner3x4Preview={banners.cover.preview}
          banner16x9Preview={banners.alternative.preview}
        />
      ),
    };
    return byStep[step] ?? null;
  };

  return (
    <PageLayout>
      <FormProvider {...form}>
        {localDraft.pendingDraft && (
          <LocalDraftBanner
            draft={localDraft.pendingDraft}
            onRestore={localDraft.restore}
            onDiscard={localDraft.discard}
          />
        )}
        <GamificationWizard<CreateGamificationFormData>
          title="Nova Campanha de Gameficação"
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          onStepChange={setCurrentStep}
          reviewStep={REVIEW_STEP}
          guardOptions={guardOptions}
          renderStep={renderStep}
          onCancel={leaveGuard.requestLeave}
          footerLeftExtra={
            <Button
              type="button"
              variant="outline"
              disabled={isSavingDraft || isCreating}
              onClick={handleSaveDraft}
            >
              <Save className="mr-2 h-4 w-4" />
              {isSavingDraft ? "Salvando..." : "Salvar rascunho"}
            </Button>
          }
          submit={{
            label: isCreating ? "Criando..." : "Criar Campanha",
            disabled: isCreating || isSavingDraft || pendingIssues.length > 0,
            title: pendingIssues.length > 0 ? pendingIssues.join(" · ") : "",
            onValid: (data) => submitAndClearDraft(data),
            onInvalid: handleInvalidSubmit,
          }}
        />
        <UnsavedChangesDialog
          open={leaveGuard.confirmOpen}
          onConfirm={leaveGuard.confirmLeave}
          onCancel={leaveGuard.cancelLeave}
        />
      </FormProvider>
    </PageLayout>
  );
};
